import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import type { AccountInfo } from '@azure/msal-browser';
import type { User } from '../models/User';
import { UserRole } from '../models/enums';
import { loginRequest, msalConfig } from '../config/msalConfig';
import { userService } from '../services/dataverse/userService';
import { apiClient } from '../services/api/apiClient';

const STORAGE_KEY = 'crm-app:auth-user';
const isMsalConfigured = Boolean(import.meta.env.VITE_CLIENT_ID && import.meta.env.VITE_TENANT_ID);

/** Map Azure AD app role claims to UserRole enum. */
const roleFromClaims = (account: AccountInfo): UserRole | null => {
  const claims = account.idTokenClaims as Record<string, unknown> | undefined;
  const roles = claims?.roles;
  if (!Array.isArray(roles)) return null;
  if (roles.includes('Admin')) return UserRole.Admin;
  if (roles.includes('Manager')) return UserRole.Manager;
  if (roles.includes('Commercial')) return UserRole.Commercial;
  return null;
};

export interface AuthContextValue {
  user: User | null;
  role: UserRole;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string>;
  acquirePricingToken: () => Promise<string>;
  acquirePowerPlatformToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState('mock-token');
  // tokenRole: derived from MSAL idTokenClaims when MSAL is configured.
  // Takes precedence over user.cr_crm_role (which could be forged via localStorage).
  const [tokenRole, setTokenRole] = useState<UserRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const msalRef = useRef<PublicClientApplication | null>(null);

  const applyUser = useCallback(async (account?: AccountInfo) => {
    if (account) {
      // Build user directly from MSAL account — no mock lookup
      const msalUser: User = {
        cr_crm_userid: account.localAccountId,
        cr_crm_fullname: account.name ?? account.username,
        cr_crm_email: account.username,
        cr_crm_azureoid: account.localAccountId,
        cr_crm_role: roleFromClaims(account) ?? UserRole.Commercial,
        cr_crm_isactive: true,
        cr_crm_lastloginat: new Date().toISOString(),
        createdon: new Date().toISOString(),
      };
      setUser(msalUser);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(msalUser));
      return;
    }
    // Mock/dev mode — use seed data
    const nextUser = await userService.getDefaultUser();
    setUser(nextUser);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(nextUser));
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        if (isMsalConfigured) {
          // MSAL mode — ignore mock localStorage, require real login
          const instance = new PublicClientApplication(msalConfig);
          await instance.initialize();
          msalRef.current = instance;
          // Registered before the user is set so child components never call the API without a token.
          apiClient.setTokenProvider(async () => {
            const acct = instance.getAllAccounts()[0];
            if (!acct) return '';
            const r = await instance.acquireTokenSilent({ scopes: loginRequest.scopes, account: acct });
            return r.idToken || r.accessToken;
          });
          const account = instance.getAllAccounts()[0];
          if (account?.username) {
            setTokenRole(roleFromClaims(account));
            await applyUser(account);
            // Re-acquire tokens silently if account already exists
            try {
              const result = await instance.acquireTokenSilent({ scopes: loginRequest.scopes, account });
              apiClient.setToken(result.idToken || result.accessToken);
            } catch {
              // Silent failed — try popup as fallback
              try {
                const result = await instance.loginPopup(loginRequest);
                apiClient.setToken(result.idToken || result.accessToken);
              } catch { /* user will need to login manually */ }
            }
            try {
              const pcResult = await instance.acquireTokenSilent({
                scopes: ['https://api.partnercenter.microsoft.com/user_impersonation'],
                account,
              });
              apiClient.setPartnerToken(pcResult.accessToken);
            } catch { /* Partner Center token optional */ }
            try {
              const pricingResult = await instance.acquireTokenSilent({
                scopes: ['https://api.partner.microsoft.com/user_impersonation'],
                account,
              });
              apiClient.setPricingToken(pricingResult.accessToken);
            } catch { /* Pricing token optional */ }
          }
          // No stored account — stay unauthenticated, show login screen
        } else {
          // Mock/dev mode — use stored or default user
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored) {
            setUser(JSON.parse(stored) as User);
          } else {
            await applyUser();
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    void init();
  }, [applyUser]);

  const login = useCallback(async () => {
    setIsLoading(true);
    try {
      if (msalRef.current) {
        const result = await msalRef.current.loginPopup(loginRequest);
        const tokenValue = result.idToken || result.accessToken || '';
        setToken(tokenValue || 'mock-token');
        apiClient.setToken(tokenValue);
        setTokenRole(roleFromClaims(result.account));
        await applyUser(result.account);

        // Acquire Partner Center token silently for delegated API access
        try {
          const pcResult = await msalRef.current.acquireTokenSilent({
            scopes: ['https://api.partnercenter.microsoft.com/user_impersonation'],
            account: result.account,
          });
          apiClient.setPartnerToken(pcResult.accessToken);
        } catch {
          console.warn('[Auth] Partner Center token not available — catalog will use mock data');
        }
        // Acquire Pricing API token (different resource)
        try {
          const pricingResult = await msalRef.current.acquireTokenSilent({
            scopes: ['https://api.partner.microsoft.com/user_impersonation'],
            account: result.account,
          });
          apiClient.setPricingToken(pricingResult.accessToken);
        } catch {
          console.warn('[Auth] Pricing API token not available — sync-pricing disabled');
        }
        return;
      }

      await applyUser();
      setToken('mock-token');
    } finally {
      setIsLoading(false);
    }
  }, [applyUser]);

  const logout = useCallback(async () => {
    setUser(null);
    localStorage.removeItem(STORAGE_KEY);
    if (msalRef.current) {
      await msalRef.current.logoutPopup();
    }
  }, []);

  const getToken = useCallback(async () => token, [token]);

  const acquirePricingToken = useCallback(async (): Promise<string> => {
    if (!msalRef.current) throw new Error('MSAL not configured');
    const account = msalRef.current.getAllAccounts()[0];
    if (!account) throw new Error('Not logged in');
    const scopes = ['https://api.partner.microsoft.com/user_impersonation'];
    try {
      const result = await msalRef.current.acquireTokenSilent({ scopes, account });
      return result.accessToken;
    } catch {
      // Silent failed — try interactive popup (consent required)
      const result = await msalRef.current.acquireTokenPopup({ scopes, account });
      return result.accessToken;
    }
  }, []);

  /** Acquires a delegated Power Platform token used to exchange for a Copilot Studio Direct Line token. */
  const acquirePowerPlatformToken = useCallback(async (): Promise<string> => {
    if (!msalRef.current) throw new Error('MSAL not configured');
    const account = msalRef.current.getAllAccounts()[0];
    if (!account) throw new Error('Not logged in');
    const scopes = ['https://api.powerplatform.com/CopilotStudio.Copilots.Invoke'];
    try {
      const result = await msalRef.current.acquireTokenSilent({ scopes, account });
      return result.accessToken;
    } catch {
      // Silent failed — try interactive popup (consent required)
      const result = await msalRef.current.acquireTokenPopup({ scopes, account });
      return result.accessToken;
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      role: tokenRole ?? user?.cr_crm_role ?? UserRole.Commercial,
      isAuthenticated: user !== null,
      isLoading,
      login,
      logout,
      getToken,
      acquirePricingToken,
      acquirePowerPlatformToken,
    }),
    [acquirePowerPlatformToken, acquirePricingToken, getToken, isLoading, login, logout, tokenRole, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
};
